import type { TadaDocumentNode } from 'gql.tada';
import useBidirectionalList from '@/hooks/useBidirectionalList';

type DateCursorItem = {
  id: string;
  datetime?: string | null;
};

type DateCursorVariables = {
  where: Record<string, unknown>;
  limit: number;
};

export default function useDateCursorBidirectionalList<
  TItem extends DateCursorItem,
  TFutureData,
  TPastData,
  TVariables extends DateCursorVariables = DateCursorVariables,
>({
  pause = false,
  anchorValue,
  baseWhere,
  directionLimit = 50,
  fieldName,
  futureQuery,
  pastQuery,
  selectFutureItems,
  selectPastItems,
  buildVariables,
}: {
  pause?: boolean;
  anchorValue: string;
  baseWhere: Record<string, unknown>;
  directionLimit?: number;
  fieldName: string;
  futureQuery: TadaDocumentNode<TFutureData, TVariables>;
  pastQuery: TadaDocumentNode<TPastData, TVariables>;
  selectFutureItems(data: TFutureData | undefined): readonly TItem[];
  selectPastItems(data: TPastData | undefined): readonly TItem[];
  buildVariables?: (args: DateCursorVariables) => TVariables;
}) {
  return useBidirectionalList<TItem, TFutureData, TPastData, TVariables>({
    pause,
    anchorValue,
    baseWhere,
    directionLimit,
    fieldName,
    futureQuery,
    pastQuery,
    selectFutureItems,
    selectPastItems,
    getItemCursor: (item) => item.datetime,
    buildVariables:
      buildVariables ??
      (({ where, limit }) =>
        ({
          where,
          limit,
        }) as TVariables),
    buildAnchorPastWhere: ({ baseWhere: whereBase, anchorValue: anchor }) => ({
      ...whereBase,
      datetime: {
        _lt: anchor,
      },
    }),
    buildAnchorFutureWhere: ({
      baseWhere: whereBase,
      anchorValue: anchor,
    }) => ({
      ...whereBase,
      datetime: {
        _gte: anchor,
      },
    }),
    buildPreviousPageWhere: ({ baseWhere: whereBase, cursor }) => ({
      ...whereBase,
      datetime: {
        _lt: cursor,
      },
    }),
    buildNextPageWhere: ({ baseWhere: whereBase, cursor }) => ({
      ...whereBase,
      datetime: {
        _gt: cursor,
      },
    }),
  });
}
